import { PropertyConfig, PropertyOptions, PropertyOptionsList } from "@cervval.js/common/src/forms/propertyConfig";
import { PropertyConfigType } from "@cervval.js/common/src/forms/propertyConfig.types";
import { anyObject, getProp } from "@cervval.js/common/src/tools/any.tools";
import { addToArrayMap } from "@cervval.js/common/src/tools/collection.tools";
import { anyReallyAny, objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { hash } from "@cervval.js/common/src/tools/string.tools";
import { TypedKey } from "../types/types.definition";

/**
 * On an given data list, filter elements based on a given callback, and returns only an array of element[key]
 * @param dataList Data list to filter and map
 * @param filter Filter callback
 * @param map Key to use for mapping
 */
export function filterMap<T, U extends T[keyof T]>(dataList: T[], filter: (a: T) => boolean, map: TypedKey<T, U>): U[] {
    const mapped: U[] = [];
    for (const data of dataList) {
        if (filter(data)) {
            mapped.push(data[map] as U);
        }
    }
    return mapped;
}

/**
 * On a given data list, filter elements based on a given callback, and returns a set of element[key].
 * @param dataList Data list to filter and map
 * @param filter Filter callback
 * @param map Key to use for mapping
 */
export function filterMapToSet<T, U extends T[keyof T]>(dataList: T[], filter: (a: T) => boolean, map: TypedKey<T, U>): Set<U> {
    const mapped: Set<U> = new Set();
    for (const data of dataList) {
        if (filter(data)) {
            mapped.add(data[map] as U);
        }
    }
    return mapped;
}

/**
 * Filter elements on a given array, and remove the others
 * @param arr Array to filter
 * @param filter Filter callback
 */
export function filterRemoveFromArray<T>(arr: T[], filter: (a: T) => boolean): void {
    for (let i = arr.length - 1; i >= 0; i--) {
        if (!filter(arr[i])) {
            arr.splice(i, 1);
        }
    }
}

/**
 * Similar as filterMap, except that nullify values will be automatically filtered.
 * @param dataList Data list to filter
 * @param map Key to use for mapping
 * @param filter Filter callback
 */
export function filterUndefinedAndMap<T, U extends T[keyof T] = T[keyof T]>(dataList: (T | undefined)[], map: TypedKey<T, U>, filter?: (a: NonNullable<T>) => boolean): U[] {
    const mapped: U[] = [];
    for (const data of dataList) {
        if (data != null) {
            if (!filter || filter(data)) {
                mapped.push(data[map] as U);
            }
        }
    }
    return mapped;
}

/**
 * On a record, add data to the underlying object behind a given key
 * @param record Record object
 * @param recordKey Record key
 * @param spreadableData Data to add. Be careful as it can override previous values of record[recordKey]
 */
export function addToRecord<K extends (number | string), V>(record: Record<K, V>, recordKey: K, spreadableData: V): void {
    record[recordKey] = { ...record[recordKey] ?? {}, ...spreadableData };
}

/** */
export function findInMap<T, U>(map: Map<T, U>, target: U): T | undefined {
    for (const [k, v] of map) {
        if (v === target) {
            return k;
        }
    }
    return undefined;
}

/** 
 * Build a cache of DTOS using key value as Map entry
 * For ID or UIDs: expect 1 DTO per key (array(1))
 * For other key, you can expect more
 */
export function getDTOCache<T, K extends keyof T>(dtos: T[], key: K): Map<NonNullable<T[K]>, T[]> {
    const cache: Map<T[K], T[]> = new Map();
    for (const dto of dtos) {
        const kValue = dto[key];
        if (kValue === undefined) { continue; }
        addToArrayMap(cache, kValue, dto);
    }
    return cache as Map<NonNullable<T[K]>, T[]>;
}

/** */
export function hashStringToNumber(str: string): number {
    let hashed = hash(str);
    while (hashed > Number.MAX_SAFE_INTEGER) {
        hashed /= 2;
    }
    return hashed;
}
/** */
export function setPCOption<T extends PropertyOptionsList | PropertyOptions>(propertyConfigs: PropertyConfig<PropertyConfigType, T>[], propertyName: string, options: T): void {
    const pc = propertyConfigs.find(v => v.propertyName === propertyName);
    if (pc == null) { return; }

    pc.options = { ...pc.options, ...options };
}

/** */
export function boolPropertySorter<T>(a: T, b: T, propertyName: TypedKey<T, boolean>, desc = true): number {
    return boolSorter(a[propertyName] as boolean, b[propertyName] as boolean, desc);
}

/** */
export function boolSorter(a: boolean, b: boolean, desc = true): number {
    if (!desc) {
        return (a ? 1 : 0) - (b ? 1 : 0);
    } else {
        return (b ? 1 : 0) - (a ? 1 : 0);
    }
}
/** */
export function addToArrayOfRecordInRecord<T extends string | number, U extends string | number, V>(arrayOfRecordInRecord: Partial<Record<T, Partial<Record<U, V[]>>>>, key1: T, key2: U, ...dataList: V[]): void {
    let levelA = arrayOfRecordInRecord[key1];
    if (levelA == null) {
        levelA = {};
        arrayOfRecordInRecord[key1] = levelA;
    }
    if (levelA[key2] == null) {
        levelA[key2] = [...dataList];
    } else {
        levelA[key2]?.push(...dataList);
    }
}


/** */
export function isObjectKey<K extends string>(data: anyReallyAny, key: K): data is Record<K, anyReallyAny> {
    return (data as anyReallyAny)[key] != null;
}

/** */
export function isPromise<T>(pon: T | Promise<T>): pon is Promise<T> {
    if (!pon) {
        return false;
    }
    if (typeof pon === "object") {
        return (pon as Promise<T>).then != null;
    } else {
        return false;
    }
}

/** */
export function hasKey<T extends anyObject, K extends string, V extends T[K]>(t: T, k: K): t is T & Record<K, V> {
    return objectKeys(t).includes(k);
}

/** */
export function hasUid<T extends anyObject>(t: T): t is T & { uid: string } {
    return getProp(t, "uid") != null;
}

/** */
export function mapGetOrCreate<K, T>(map: Map<K, T>, key: K, initializer: () => T): T {
    let value = map.get(key);
    if (value == null) {
        value = initializer();
        map.set(key, value);
    }
    return value;
}
/** */
export function addPropertyRecursively<T, K extends keyof T, KID extends keyof T, TKID extends T[KID] & string | number>(params: {
    data: T,
    dataList: T[],
    parentKey: keyof T,
    idKey: KID,
    property: K

}, _cache?: Map<TKID, T[]>,
    _result: T[K][] = []
): T[K][] {
    _result.push(params.data[params.property]);
    if (_cache == null) {
        _cache = getDTOCache(params.dataList, params.idKey) as Map<TKID, T[]>;
    }
    const parents = _cache.get(params.data[params.parentKey] as TKID);
    if (parents) {
        for (const parent of parents) {
            addPropertyRecursively({ ...params, data: parent }, _cache, _result);
        }
    }
    return _result;
}

/**
 * @param toTest string to test
 * @param criterias array of string or regexp to test
 * @returns true if toTest matches all criteria
 */
export function matchesAllExpressions(toTest: string, criterias: (string | RegExp)[]): boolean {
    if (criterias) {
        for (const i of criterias) {
            if (i instanceof RegExp) {
                if (!i.test(toTest)) {
                    // file matches ignored regexp
                    return false;
                }
            } else if (i !== toTest) {
                // file matches ignored folder name
                return false;
            }
        }
        return true;
    } else {
        return false;
    }
}

type NR<T> = { [key: string | number]: T | NR<T> };
/** */
export function sumInObjectValues<T extends NR<number>>(objects: T[], buffer: Record<string, number> = {}): Record<string, number> {
    for (const object of objects) {
        if (object == null) { continue; }
        for (const k of objectKeys(object)) {
            const ks = k as string;
            const v = object[k];
            if (typeof v === "number") {
                buffer[ks] = (buffer[ks] ?? 0) + v;
            } else if (typeof v === "object") {
                sumInObjectValues([v], buffer);
            }
        }
    }
    return buffer;
}

/**
 * Search for object located at the given index in the provided array.
 * If the given index is negative, the search will begin at the end of the array, and go back to previous elements.
 * @param arr Array of objects
 * @param i Object index
 * @returns The object located at the array at the given index, or undefined if params are invalid or search is out of bound
 */
export function at<T>(arr?: T[], i?: number): T | undefined {
    if (arr == null || i == null) { return undefined; }
    const index = i < 0 ? arr.length + i : i;
    return arr[index];
}

